/**
 * Client for the platform-admin Specialists family
 * (`GET /api/platform/specialists*`, lexserver-owned, platform-admin JWT).
 *
 * ⚠ THE SHAPE THAT DRIVES EVERY COMPONENT: each per-row column is a
 * DISCRIMINATED UNION — `{status:"known", …payload}` or
 * `{status:"unknown", reason}`. There is no `null` and nothing falls back to a
 * default, deliberately: a roster that rendered an unreported manifest or a
 * broken home as healthy would be confidently wrong at exactly the moment
 * something is wrong. TypeScript forces the branch; `reason` is written for an
 * operator reading the row cold and must reach the UI, never a bare em-dash.
 *
 * This is the same rule the Agent Bus tab had to learn by shipping its inverse
 * (`lib/bus-section-state.ts`), except here the SERVER refuses to let the
 * client make the mistake. Do not add a `?? default` anywhere in this file.
 *
 * Transport note (the 2026-08-19 clarification this section is built on): this
 * reaches a PLATFORM-OWNED share through lexserver's own API — NOT the runtime
 * fs/git proxy that `RuntimeFsBrowser`/`RuntimeGitBrowser` use. Rendering is
 * reused from those; transport is not.
 */

import { getLexiunToken } from "./lexiun-jwt";
import { newTraceparent } from "./traceparent";

const LEXSERVER_BASE = "/lexserver";
const BASE = "/api/platform/specialists";

/** The union every column uses. Branch on `status` before touching a payload. */
export type Known<T> = { status: "known" } & T;
export type Unknown = { status: "unknown"; reason: string };
export type Col<T> = Known<T> | Unknown;

export interface SpecialistPromptRow {
  slug: string;
  present: boolean;
  latestVersion: number | null;
  productionVersion: number | null;
  disabled: boolean;
}

export interface SpecialistAttributes {
  values: {
    domain?: string;
    practiceArea?: string;
    listing?: string;
    /**
     * The per-specialist half of the double gate that decides whether the
     * SpecialistPoller wakes this specialist (the other half is the runtime's
     * `BUS_SPECIALIST_POLL=1`, which no payload can see — so "opted in" here
     * never means "consults are dispatching").
     *
     * 🚨 **DO NOT RENAME — this is a deliberate BOUNDARY MAPPING, not drift.**
     * The MANIFEST field was renamed `specialist.pollerOptIn` -> `bus.servable`
     * (`9605c3973`, 2026-08-23: servability is a BUS property, and
     * `specialist{}`'s sibling `practiceArea` is a pt-BR legal slug a non-legal
     * service harness cannot honestly claim). **The WIRE name deliberately did
     * NOT follow** — harn-service's `registry-reporter.ts` maps
     * `pollerOptIn: h.manifest.bus?.servable ?? false`, so this SPA and
     * lexserver need no change and no lockstep deploy. Justified, not merely
     * convenient: the mirror is specialist-scoped by construction, so
     * `pollerOptIn` is an accurate name for what is mirrored across that seam.
     *
     * ⚠ Renaming it here to "match the manifest" is NOT cosmetic, and it fails
     * SILENTLY in both directions:
     *   - **This side alone** — nothing validates the payload at runtime, so
     *     the read yields `undefined`, the roster cell falls to its `: "out"`
     *     branch, and EVERY specialist renders opted-OUT while the column looks
     *     perfectly healthy. `?` is defensive spelling only; the server
     *     declares the field REQUIRED, so under today's contract a `known`
     *     attributes column always carries it.
     *   - **The producer alone** — `lexserver/src/db/models/specialist-registry.ts`
     *     declares `pollerOptIn: z.boolean()` REQUIRED and that file carries the
     *     twin of this note; drop the name upstream and the ingest write fails,
     *     freezing the mirror on its last good snapshot while the S1 roster
     *     keeps rendering it as current.
     * Any rename therefore needs the PRODUCER to land first, then this.
     */
    pollerOptIn?: boolean;
  };
  manifestHash?: string;
  reportedAt?: string;
  reportedBy?: string;
}

export interface SpecialistWorkspaceStats {
  kbDocs: number;
  memoryDocs: number;
  wikiDocs: number;
}

export interface SpecialistRow {
  specialistId: string;
  /** False = present in the registry but absent from the home roster. */
  inRoster: boolean;
  attributes: Col<{ values: SpecialistAttributes["values"] } & Omit<SpecialistAttributes, "values">>;
  prompt: Col<{ row: SpecialistPromptRow }>;
  workspace: Col<{ stats: SpecialistWorkspaceStats }>;
  applyMode: Col<{ mode: "staged" | "auto-with-oversight" | string }>;
}

/**
 * The platform home's own state (S7), exactly as lexserver models it.
 *
 * ⚠ THE ⚖ SPLIT THAT MUST SURVIVE EVERY RENDER: `disabled` and `broken` are
 * NOT two shades of the same thing. `disabled` is a legitimate deployment — no
 * root configured, the feature is off, nothing is wrong. `broken` is a fault an
 * operator has to act on. Both refuse every consumer surface, which is exactly
 * why a UI that folds them together tells an operator to page someone at 3am
 * over a deployment choice, or worse, stays quiet through a real fault.
 *
 * ⚠ AND `disabled` IS ALSO THE SERVER'S PRE-BOOT DEFAULT (`specialist-home.ts`:
 * "It starts as `disabled` rather than `undefined` so a surface that reads it
 * before boot has run REFUSES rather than throwing"). That is the right server
 * default and the wrong thing for a client to conflate with a failed READ —
 * see `selectHomeView` in `lib/specialist-home-state.ts`, which keeps
 * could-not-read as its own outcome rather than letting it land here.
 */
export type SpecialistHomeState =
  | { state: "disabled" }
  | { state: "broken"; root: string; reason: string }
  | {
      state: "ready";
      root: string;
      sharedPath: string;
      roster: string[];
      /** Specialist folders created by the boot run; empty on a converged tree. */
      created: string[];
      /** Commit sha when that run committed, or null when nothing changed. */
      commit: string | null;
    };

/** What the roster route embeds. Widened with `string` because the roster is a
 *  SUMMARY: a state this client does not recognise must stay representable
 *  rather than being coerced into one it does. */
export interface SpecialistsHome {
  state: SpecialistHomeState["state"] | string;
  root?: string;
  sharedPath?: string;
  roster?: string[];
  created?: string[];
  commit?: string | null;
  reason?: string;
}

export interface SpecialistsResult {
  home: SpecialistsHome;
  basePrompt: Col<{ row: SpecialistPromptRow }>;
  /** True when ANY column on ANY row is `unknown` — the server's own verdict.
   *  Render an incomplete-view banner on it; do NOT re-derive it by scanning
   *  rows, which is how a client and a server start disagreeing about health. */
  degraded: boolean;
  rows: SpecialistRow[];
}

export class SpecialistsApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "SpecialistsApiError";
  }
}

/**
 * Operator-facing sentence for a specialists-API failure.
 *
 * ⚠ CODE FIRST, STATUS SECOND. The typed codes are the contract lexserver
 * publishes (`PATH_ESCAPE` · `FORBIDDEN_PATH` · `NOT_FOUND` · `TOO_LARGE` ·
 * `NOT_A_FILE` · `HOME_UNAVAILABLE`); the HTTP status is derived from them, and
 * two codes can share one status while meaning different things to an operator.
 * Branching on the status would merge them back together — which is how the S1
 * guard that trusts `ok:false` over the status got written in the first place.
 *
 * For the tree family the SERVER's message is the better sentence (it names the
 * actual path), so it is preserved verbatim; this function adds only the
 * CONSEQUENCE the message cannot carry — chiefly that an oversized file was
 * refused whole rather than truncated.
 */
export function describeSpecialistsError(e: SpecialistsApiError): string {
  switch (e.code) {
    case "ROUTE_NOT_FOUND":
      return "This lexserver does not serve the specialists API yet.";
    case "TOO_LARGE":
      // The one place a bare server message would under-inform: an operator who
      // reads "over the limit" next to an empty pane may reasonably assume they
      // are looking at a truncated head. They are not — there is no content at all.
      return `${e.message}. It was refused whole, NOT truncated — nothing below is a partial read.`;
    case "HOME_UNAVAILABLE":
      return `${e.message} (see Health for whether the home is disabled or broken — they are different situations).`;
    case "PATH_ESCAPE":
    case "FORBIDDEN_PATH":
      return `${e.message}. This is a refusal, not an absence — the API never serves that path.`;
    case "NOT_A_FILE":
    case "NOT_FOUND":
      // The server names the actual specialist or path; a generic sentence here
      // would discard the one useful detail it went out of its way to send —
      // the same mistake as rendering a known-unknown column as an em-dash.
      // Fall back only when it said nothing at all.
      return e.message?.trim() || "No such specialist or path on this platform.";
    default:
      break;
  }
  switch (e.status) {
    case 401:
    case 403:
      return "Not authorized for the platform specialists API.";
    case 404:
      return "No such specialist on this platform.";
    default:
      return e.message || "The specialists API could not be read.";
  }
}

/** Serialize a query. Exported for unit test: an omitted-vs-empty mistake here
 *  is invisible in the UI and shows up as the server reading a DIFFERENT path
 *  than the one on screen. `path=""` is MEANINGFUL to the tree route (it means
 *  the workspace root), so an empty string is kept when the caller passed one
 *  and only `undefined` is dropped. */
export function buildSpecialistQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

async function request<T>(path: string): Promise<T> {
  const token = await getLexiunToken();
  const headers: Record<string, string> = { traceparent: newTraceparent() };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${LEXSERVER_BASE}${path}`, { headers });

  const raw = await res.text();
  let body: { ok?: boolean; data?: unknown; error?: { code?: string; message?: string } | string; message?: string } = {};
  try {
    body = JSON.parse(raw) as typeof body;
  } catch {
    // An unmatched lexserver route answers text/plain, not JSON — the trap that
    // once turned a missing ROUTE into an opaque SyntaxError. Keep them distinct:
    // "this server cannot answer" and "this thing is gone" need different words.
    throw new SpecialistsApiError(
      res.status === 404 ? "ROUTE_NOT_FOUND" : "NON_JSON_RESPONSE",
      raw.slice(0, 200) || res.statusText,
      res.status,
    );
  }
  if (!res.ok || body.ok === false) {
    const err = typeof body.error === "object" ? body.error : undefined;
    throw new SpecialistsApiError(
      err?.code ?? (typeof body.error === "string" ? body.error : "SPECIALISTS_ERROR"),
      err?.message ?? body.message ?? res.statusText,
      res.status,
    );
  }
  return body.data as T;
}

async function post<T>(path: string): Promise<T> {
  const token = await getLexiunToken();
  const headers: Record<string, string> = { traceparent: newTraceparent() };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${LEXSERVER_BASE}${path}`, { method: "POST", headers });

  const raw = await res.text();
  let body: { ok?: boolean; data?: unknown; error?: { code?: string; message?: string } | string; message?: string } = {};
  try {
    body = JSON.parse(raw) as typeof body;
  } catch {
    throw new SpecialistsApiError(
      res.status === 404 ? "ROUTE_NOT_FOUND" : "NON_JSON_RESPONSE",
      raw.slice(0, 200) || res.statusText,
      res.status,
    );
  }
  if (!res.ok || body.ok === false) {
    const err = typeof body.error === "object" ? body.error : undefined;
    throw new SpecialistsApiError(
      err?.code ?? (typeof body.error === "string" ? body.error : "SPECIALISTS_ERROR"),
      err?.message ?? body.message ?? res.statusText,
      res.status,
    );
  }
  return body.data as T;
}

/** The roster + per-specialist state aggregation (S1, and S2's view half). */
export function getSpecialists(): Promise<SpecialistsResult> {
  return request<SpecialistsResult>(BASE);
}

/** One row. 404 when the id is unknown to the platform. */
export function getSpecialist(id: string): Promise<SpecialistRow> {
  return request<SpecialistRow>(`${BASE}/${encodeURIComponent(id)}`);
}

/** The raw manifest mirror — the diagnostic for a column reading `unknown`. */
export function getSpecialistsRegistry(): Promise<unknown> {
  return request<unknown>(`${BASE}/registry`);
}

// ── S4 (view) + S7 — the workspace tree and the home's own state ──────────
//
// lexserver slice 3 (`1da68cd19`). Read-only by design: kb WRITE is slice 4,
// and `lib/specialist-tier.ts` holds the tier rule the write half will land in.

/** One entry in a workspace listing. Directories carry no size/mtime. */
export interface SpecialistTreeEntry {
  /** Workspace-relative, POSIX-separated. */
  path: string;
  name: string;
  type: "file" | "dir";
  size?: number;
  modifiedAt?: string;
}

export interface SpecialistTreeListing {
  path: string;
  entries: SpecialistTreeEntry[];
}

export interface SpecialistFileContent {
  path: string;
  size: number;
  modifiedAt: string;
  content: string;
}

/**
 * One commit touching one file. METADATA ONLY, deliberately — no diff, no blob.
 *
 * Revert is forward-apply (`Lex-Reverts:<sha>`), so nothing in this surface
 * needs historical CONTENT, and withholding it keeps a line removed by a later
 * commit from being retrievable through the UI. Do not "enrich" this by
 * fetching a diff from somewhere else: the omission is the feature.
 */
export interface SpecialistCommit {
  sha: string;
  authoredAt: string;
  subject: string;
}

export interface SpecialistHistory {
  path: string;
  commits: SpecialistCommit[];
}

/** S7 — the substrate's own state. Refuses rather than throwing when unset. */
export function getSpecialistHomeState(): Promise<{ home: SpecialistHomeState }> {
  return request<{ home: SpecialistHomeState }>(`${BASE}/state`);
}

/** List one directory of a specialist's workspace. `""` is the workspace root. */
export function getSpecialistTree(id: string, path = ""): Promise<SpecialistTreeListing> {
  return request<SpecialistTreeListing>(
    `${BASE}/${encodeURIComponent(id)}/tree${buildSpecialistQuery({ path })}`,
  );
}

/** Read one file whole. Over 512 KiB the server REFUSES (413) — it never truncates. */
export function getSpecialistFile(id: string, path: string): Promise<SpecialistFileContent> {
  return request<SpecialistFileContent>(
    `${BASE}/${encodeURIComponent(id)}/file${buildSpecialistQuery({ path })}`,
  );
}

/** Per-file commit history, newest first. `limit` defaults to 50 server-side (max 200). */
export function getSpecialistHistory(
  id: string,
  path: string,
  limit?: number,
): Promise<SpecialistHistory> {
  return request<SpecialistHistory>(
    `${BASE}/${encodeURIComponent(id)}/history${buildSpecialistQuery({ path, limit })}`,
  );
}

// ── S7 jobs + per-commit stat (lexserver `865349001`) ────────────────────

/**
 * One maintenance job's last tick.
 *
 * ⚠ `null` — NO OUTCOME RECORDED IN THIS PROCESS — is a THIRD state, not a
 * shade of `ok`. It is deliberately NOT described as "never ran": the record is
 * an in-process Map, so any lexserver restart wipes all outcomes back to `null`
 * and a UI claiming "never ran" false-alarms on every routine deploy.
 * lexserver states it as a contract and asserts it server-side: collapsing it
 * into `ok` turns "no news" into "good news" on the surface an operator
 * consults precisely when something already feels wrong. Same shape as the
 * roster's `unknown`, and the reason it matters here is sharper than usual:
 * before this endpoint existed, the only observable was that TIMERS had been
 * created — and a job throwing on every tick creates timers exactly as happily
 * as a healthy one, so the surface was structurally incapable of reporting a
 * fault. Do not add a `?? "ok"` anywhere near this type.
 */
export interface SpecialistJobTick {
  lastRunAt: string;
  outcome: "ok" | "failed" | string;
  durationMs?: number;
  error?: string;
}

export interface SpecialistJobs {
  /**
   * When this lexserver process started — the denominator that tells an
   * EXPECTED missing outcome (restarted moments ago) from an ALARMING one (up
   * for hours, the job still never fired). Optional because an older lexserver
   * will not send it; `outcomeGap()` answers "cannot say" rather than anything
   * reassuring when it is absent.
   *
   * ⚠ Briefly read under TWO names. While the field was in flight lexserver
   * offered `processStartedAt` and the ⚖ ruling requiring it said `since`, so
   * this client read both — guessing wrong here would have degraded SILENTLY
   * back into the false-alarm rendering the field exists to prevent, which is
   * the only condition that justifies carrying two names. `since` shipped and
   * lexserver has since confirmed by CONTRACT that `processStartedAt` was never
   * shipped and never will be (live payload keys: `intervals`, `jobs`,
   * `scheduled`, `since` — measured on both sides), so the fallback is deleted
   * rather than left as a shim nobody dares remove.
   */
  since?: string;
  /** A fact about TIMERS ONLY — deliberately independent of the outcomes.
   *  Armed-while-every-tick-fails is exactly what this surface exists to show. */
  scheduled: boolean;
  /** So "last ran 9 hours ago" can be judged normal or alarming — a timestamp
   *  alone cannot say which. */
  intervals: { drainMs?: number; dailyMs?: number };
  jobs: Record<string, SpecialistJobTick | null>;
}

/** One file's churn in a commit. `added`/`removed` are `null` for a BINARY file
 *  (git reports `-`) — do not assume numbers. */
export interface SpecialistCommitFile {
  path: string;
  added: number | null;
  removed: number | null;
}

/**
 * A commit's STAT — paths and counts, never content.
 *
 * This is the compromise that closed the revert-blind gap without giving up
 * §6.4's property: a count is not a line, so a line removed by a later commit
 * stays unretrievable through this UI. ⚠ One disclosure lexserver stated rather
 * than let a consumer discover: a commit that DELETED a file reveals that
 * file's PATH, which the tree no longer shows. Deliberate — it is often the
 * most decision-relevant fact about a revert — but it is a real widening.
 */
export interface SpecialistCommitStat {
  sha: string;
  files: SpecialistCommitFile[];
}

/** S7's jobs slot. Platform-wide, not per-specialist. */
export function getSpecialistJobs(): Promise<SpecialistJobs> {
  return request<SpecialistJobs>(`${BASE}/jobs`);
}

/** The stat for one commit in one specialist's workspace. */
export function getSpecialistCommitStat(id: string, sha: string): Promise<SpecialistCommitStat> {
  return request<SpecialistCommitStat>(
    `${BASE}/${encodeURIComponent(id)}/commit${buildSpecialistQuery({ sha })}`,
  );
}

// ── ⚖ S3 composition gate (§4-ter) ───────────────────────────────────────
//
// Gates the PRODUCTION LABEL MOVE of a `harn:sp-<name>` persona — not the
// enable verb, and not authoring. A candidate version may be written freely;
// what needs both tiers green is making it the version every request resolves.

export interface TierAFinding {
  assertion: string;
  verdict: "fail" | "advisory" | string;
  offset?: number;
  excerpt?: string;
}

export interface TierBFinding {
  assertion: string;
  severity: "fail" | "advisory" | string;
  personaQuote?: string;
  counterpartQuote?: string;
  counterpartSource?: "base" | "domain" | string;
  rationale?: string;
}

export interface CompositionVerdict {
  key: {
    specialistId: string;
    baseVersion: number;
    manifestHash: string;
    personaVersion: number;
  };
  /** ⚠ `null` is the honest THIRD state — no verdict recorded for this triple
   *  yet. Never pass, never failure. Same discipline as the jobs panel's null,
   *  and the same trap: one value, two readings, only one of them alarming. */
  verdict: "pass" | "fail" | "incomplete" | null;
  tierA: TierAFinding[];
  tierB: TierBFinding[];
  incompleteReason: "DOMAIN_UNAVAILABLE" | "JUDGE_INVALID" | string | null;
  checkedAt: string | null;
  /** Server-side explanation when Tier A short-circuited Tier B. An empty
   *  `tierB` with no reason reads as "the judge found nothing". */
  tierBSkipped?: string;
}

export interface TierAResult {
  tier: "A";
  baseVersion: number;
  personaVersion: number;
  /** Derived from the CURRENT base at check time, never hardcoded. */
  reservedRegions: string[];
  findings: TierAFinding[];
  pass: boolean;
}

/**
 * Look up the RECORDED verdict for a triple. A pure read — safe on production
 * rows, and the only gate call this UI may make without an operator asking.
 *
 * ⚠⚠ `baseVersion` IS REQUIRED, AND OMITTING IT IS SILENT. It was optional here
 * for about an hour and the gate was unusable the whole time: the route reads
 * `Number(query.baseVersion ?? 0)`, so a missing param looks up the triple
 * `(0, hash, personaVersion)` — which NO judge run can ever record, because the
 * judge records against the real base version. The endpoint then answers 200
 * with `verdict: null`, i.e. a perfectly legitimate-looking "not yet checked",
 * for a question that can never be answered. Caught only by running the judge
 * live and watching the panel stay `unchecked` afterwards.
 *
 * Fail-closed is what kept it merely broken instead of dangerous — the gate
 * blocked rather than passed — but the state it reported was FALSE, which on
 * this surface is its own defect. The parameter is mandatory now so the type
 * system asks the question the wire silently defaulted.
 */
export function getCompositionVerdict(
  specialistId: string,
  personaVersion: number,
  baseVersion: number,
): Promise<CompositionVerdict> {
  return request<CompositionVerdict>(
    `${BASE}/${encodeURIComponent(specialistId)}/composition-check${buildSpecialistQuery({
      personaVersion,
      baseVersion,
    })}`,
  );
}

/**
 * Tier A only — structural, deterministic, ~20 ms, and NON-PERSISTING.
 *
 * Safe to run on a live persona because it records nothing. Useful precisely
 * because it lets an author clear structural problems without spending a judge
 * call or, more importantly, without minting an approval artifact.
 */
export function runTierA(specialistId: string, personaVersion: number): Promise<TierAResult> {
  return post<TierAResult>(
    `${BASE}/${encodeURIComponent(specialistId)}/composition-check${buildSpecialistQuery({
      personaVersion,
    })}`,
  );
}

/**
 * The full check — Tier A then the LLM judge — which **RECORDS a verdict**.
 *
 * ⚠⚠ A RECORDED VERDICT IS AN APPROVAL ARTIFACT (§4-ter, added from lexserver's
 * own restraint). A recorded `pass` is what the promotion gate consults, so
 * running this casually MANUFACTURES AN APPROVAL for a persona nobody reviewed.
 * It must therefore never fire on page load, never on selection, never as a
 * retry, and never as a side effect of anything — only from an explicit
 * operator action that says what it is about to do. The UI treats it as a
 * write, because it is one.
 */
export function runCompositionCheck(
  specialistId: string,
  personaVersion: number,
): Promise<CompositionVerdict> {
  return post<CompositionVerdict>(
    `${BASE}/${encodeURIComponent(specialistId)}/composition-check/judge${buildSpecialistQuery({
      personaVersion,
    })}`,
  );
}
